import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { BellRing, Megaphone, Pin, Send, Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';
import { notificationsAPI } from '../../services/api';
import { useAuthStore } from '../../store/authStore';

const formatNoticeDate = (value) => {
  if (!value) return '';
  return new Date(value).toLocaleString();
};

const toDateKey = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const getNoticeGroupKey = (notice = {}) => {
  if (notice.related_to?.noticeGroupId) return notice.related_to.noticeGroupId;

  const createdAt = String(notice.created_at || '').slice(0, 19);
  return [
    notice.title || '',
    notice.message || '',
    createdAt,
    notice.related_to?.startDate || '',
    notice.related_to?.endDate || '',
  ].join('::');
};

const getNotificationActionPath = (notice = {}) =>
  notice.related_to?.actionPath
  || notice.related_to?.actionUrl
  || notice.related_to?.url
  || notice.action_url
  || '';

const NoticeCard = ({ notice, onOpen, adminSummary, showViewerMeta }) => {
  const isUnread = !notice.is_read;
  const hasAction = Boolean(getNotificationActionPath(notice));

  return (
    <button
      type="button"
      onClick={() => onOpen(notice)}
      className={`w-full rounded-xl border p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${
        isUnread ? 'border-amber-200 bg-amber-50/70' : 'border-gray-200 bg-white'
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className={`mt-1 rounded-lg p-2 ${isUnread ? 'bg-amber-200 text-amber-900' : 'bg-blue-100 text-blue-700'}`}>
            <Megaphone className="h-5 w-5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold text-gray-900">{notice.title}</h3>
              {isUnread && (
                <span className="inline-flex items-center rounded-full bg-amber-200 px-2 py-0.5 text-xs font-semibold text-amber-900">
                  New
                </span>
              )}
            </div>
            <p className="mt-2 line-clamp-3 text-sm leading-6 text-gray-600">{notice.message}</p>
          </div>
        </div>
        <Pin className={`h-4 w-4 shrink-0 ${isUnread ? 'text-amber-700' : 'text-gray-300'}`} />
      </div>
      <div className="mt-4 flex items-center justify-between text-xs text-gray-500">
        <span>Posted on {formatNoticeDate(notice.created_at)}</span>
        <span>{hasAction ? 'Open request' : notice.type === 'system' ? 'Notice Board' : notice.type}</span>
      </div>
      {showViewerMeta && adminSummary && (
        <div className="mt-2 text-[11px] leading-5 text-gray-500">
          <span className="font-medium text-gray-600">
            Viewed by {adminSummary.viewedCount}/{adminSummary.totalRecipients}
          </span>
          {adminSummary.viewers?.length > 0 && (
            <span> • {adminSummary.viewers.map((viewer) => viewer.name).join(', ')}</span>
          )}
        </div>
      )}
    </button>
  );
};

const Notifications = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuthStore();
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState(new Date().toISOString().slice(0, 10));
  const [selectedNotice, setSelectedNotice] = useState(null);
  const [postedFromDate, setPostedFromDate] = useState('');
  const [postedToDate, setPostedToDate] = useState('');

  const { data: notificationsData, isLoading } = useQuery(
    ['notifications'],
    () => notificationsAPI.getAll({ limit: 100 }),
    {
      retry: false,
      refetchOnWindowFocus: false,
      refetchInterval: false,
      staleTime: 5 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
    }
  );

  const { data: boardSummaryData } = useQuery(
    ['noticeBoardSummary'],
    () => notificationsAPI.getBoardSummary(),
    {
      enabled: isAdmin(),
      retry: false,
      refetchOnWindowFocus: false,
      refetchInterval: false,
      staleTime: 5 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
    }
  );

  const notices = useMemo(
    () => notificationsData?.data?.data || [],
    [notificationsData]
  );

  const filteredNotices = useMemo(() => {
    if (!postedFromDate && !postedToDate) return notices;
    return notices.filter((notice) => {
      const dateKey = toDateKey(notice.created_at);
      if (!dateKey) return false;
      if (postedFromDate && dateKey < postedFromDate) return false;
      if (postedToDate && dateKey > postedToDate) return false;
      return true;
    });
  }, [notices, postedFromDate, postedToDate]);

  const boardSummaryMap = useMemo(
    () => new Map((boardSummaryData?.data?.data || []).map((entry) => [entry.noticeGroupId, entry])),
    [boardSummaryData]
  );

  useEffect(() => {
    notificationsAPI.markAllAsRead()
      .then(() => {
        queryClient.invalidateQueries('notifications');
        queryClient.invalidateQueries('unreadNotifications');
        queryClient.invalidateQueries('noticeBoardSummary');
      })
      .catch(() => {});
  }, [queryClient]);

  const publishNotice = useMutation(
    (payload) => notificationsAPI.broadcast(payload),
    {
      onSuccess: (response) => {
        toast.success(`Notice published to ${response.data?.recipients || 0} users`);
        setTitle('');
        setMessage('');
        queryClient.invalidateQueries('notifications');
        queryClient.invalidateQueries('unreadNotifications');
        queryClient.invalidateQueries('noticeBoardSummary');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to publish notice');
      },
    }
  );

  const markAsRead = useMutation(
    (id) => notificationsAPI.markAsRead(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('notifications');
        queryClient.invalidateQueries('unreadNotifications');
        queryClient.invalidateQueries('noticeBoardSummary');
      },
    }
  );

  const handleOpenNotice = (notice) => {
    if (!notice.is_read) {
      markAsRead.mutate(notice.id);
    }

    const actionPath = getNotificationActionPath(notice);
    if (actionPath) {
      navigate(actionPath);
      return;
    }

    setSelectedNotice(notice);
  };

  const handlePublish = (event) => {
    event.preventDefault();
    if (!title.trim() || !message.trim()) {
      toast.error('Notice title and message are required');
      return;
    }

    publishNotice.mutate({
      title: title.trim(),
      message: message.trim(),
      startDate,
      endDate,
    });
  };

  const unreadCount = filteredNotices.filter((notice) => !notice.is_read).length;
  const selectedNoticeSummary = selectedNotice ? boardSummaryMap.get(getNoticeGroupKey(selectedNotice)) : null;

  return (
    <div className="space-y-6">
      <div className="rounded-[2rem] bg-gradient-to-r from-amber-100 via-orange-50 to-white p-6 shadow-sm ring-1 ring-amber-100">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-white/80 px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-amber-700">
              <Sparkles className="h-3.5 w-3.5" />
              Notifications
            </div>
            <h1 className="mt-3 text-3xl font-bold tracking-tight text-gray-900">Notifications & Notice Board</h1>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <div className="rounded-lg bg-white px-4 py-3 shadow-sm ring-1 ring-gray-100">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Total Items</p>
              <p className="mt-2 text-2xl font-bold text-gray-900">{filteredNotices.length}</p>
            </div>
            <div className="rounded-lg bg-white px-4 py-3 shadow-sm ring-1 ring-gray-100">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Unread</p>
              <p className="mt-2 text-2xl font-bold text-amber-700">{unreadCount}</p>
            </div>
            <div className="rounded-lg bg-white px-4 py-3 shadow-sm ring-1 ring-gray-100 sm:col-span-1 col-span-2">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Viewer</p>
              <p className="mt-2 truncate text-base font-semibold text-gray-900">{user?.name}</p>
            </div>
          </div>
        </div>
      </div>

      {isAdmin() && (
        <form onSubmit={handlePublish} className="card space-y-4">
          <div className="flex items-center gap-2">
            <BellRing className="h-5 w-5 text-amber-600" />
            <h2 className="text-lg font-semibold text-gray-900">Publish New Notice</h2>
          </div>
          <div className="grid grid-cols-1 gap-4">
            <input
              type="text"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="Notice title"
              className="input"
              maxLength={255}
            />
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <input
                type="date"
                value={startDate}
                onChange={(event) => setStartDate(event.target.value)}
                className="input"
              />
              <input
                type="date"
                value={endDate}
                min={startDate}
                onChange={(event) => setEndDate(event.target.value)}
                className="input"
              />
            </div>
            <textarea
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              placeholder="Write the board notice for all users..."
              className="input min-h-[140px] resize-y"
            />
          </div>
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={publishNotice.isLoading}
              className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-4 py-2 font-medium text-white transition hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Send className="h-4 w-4" />
              {publishNotice.isLoading ? 'Publishing...' : 'Publish Notice'}
            </button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.35fr_0.85fr]">
        <div className="card">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Notifications</h2>
              <p className="text-sm text-gray-500">Admin notices and action requests appear here.</p>
            </div>
          </div>
          <div className="mb-4 grid grid-cols-1 gap-3 lg:grid-cols-3">
            <div>
              <label className="label">Posted From</label>
              <input
                type="date"
                value={postedFromDate}
                max={postedToDate || undefined}
                onChange={(event) => setPostedFromDate(event.target.value)}
                className="input"
              />
            </div>
            <div>
              <label className="label">Posted To</label>
              <input
                type="date"
                value={postedToDate}
                min={postedFromDate || undefined}
                onChange={(event) => setPostedToDate(event.target.value)}
                className="input"
              />
            </div>
            <div className="flex items-end">
              <button
                type="button"
                className="btn-secondary w-full"
                onClick={() => {
                  setPostedFromDate('');
                  setPostedToDate('');
                }}
              >
                Clear Date Range
              </button>
            </div>
          </div>
          {isLoading ? (
            <p className="py-10 text-center text-gray-500">Loading notifications...</p>
          ) : filteredNotices.length === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-6 py-14 text-center">
              <p className="text-lg font-semibold text-gray-700">No notifications yet</p>
              <p className="mt-2 text-sm text-gray-500">Admin notices and approval requests will appear here.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {filteredNotices.map((notice) => (
                <NoticeCard
                  key={notice.id}
                  notice={notice}
                  onOpen={handleOpenNotice}
                  showViewerMeta={isAdmin()}
                  adminSummary={boardSummaryMap.get(getNoticeGroupKey(notice))}
                />
              ))}
            </div>
          )}
        </div>

        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900">Notification Preview</h2>
          <p className="mt-1 text-sm text-gray-500">Open any notice to read the full message. Action requests open their related page directly.</p>
          {selectedNotice ? (
            <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50/70 p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">{selectedNotice.title}</h3>
                  <p className="mt-1 text-xs uppercase tracking-[0.2em] text-amber-700">Notice Board</p>
                </div>
                {!selectedNotice.is_read && (
                  <span className="rounded-full bg-amber-200 px-2 py-1 text-xs font-semibold text-amber-900">
                    Unread
                  </span>
                )}
              </div>
              <p className="mt-4 whitespace-pre-wrap text-sm leading-7 text-gray-700">{selectedNotice.message}</p>
              <div className="mt-5 space-y-1 text-xs text-gray-500">
                <p>Posted on {formatNoticeDate(selectedNotice.created_at)}</p>
                <p>
                  Active from {selectedNotice.related_to?.startDate || '-'} to {selectedNotice.related_to?.endDate || '-'}
                </p>
                {isAdmin() && selectedNoticeSummary && (
                  <p>
                    Viewed by {selectedNoticeSummary.viewedCount}/{selectedNoticeSummary.totalRecipients}
                    {selectedNoticeSummary.viewers?.length > 0 && (
                      <> • {selectedNoticeSummary.viewers.map((viewer) => viewer.name).join(', ')}</>
                    )}
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="mt-5 rounded-xl border border-dashed border-gray-200 bg-gray-50 px-6 py-12 text-center">
              <p className="text-sm text-gray-500">Select a notice card to view its full content here.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Notifications;
